import React, {
  useState,
  useEffect,
  Suspense,
  lazy,
} from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import { Auth } from 'aws-amplify';
import { useStoreState, useStoreActions } from 'easy-peasy';

/* APIs */
import * as organization from 'apis/organization';
import { can } from 'helpers';

/* Components */
import MainLayout from 'layouts/Main';
import PublicLayout from 'layouts/Public';
import { useLocation, matchPath } from 'react-router-dom';

const NotFound = lazy(() => import('containers/NotFound'));
const Unauthorised = lazy(() => import('containers/Unauthorised'));
const Forbidden = lazy(() => import('containers/Forbidden'));
const WorkerNotFound = lazy(() => import('containers/WorkerNotFound'));
const CompleteSignup = lazy(() => import('containers/CompleteSignup'));
const SelectOrgAndChildTenant = lazy(() => import('containers/SelectOrgAndChildTenant'));
const RouteWithLayout = lazy(() => import('components/shared/RouteWithLayout'));
const Authentication = lazy(() => import('containers/Authentication'));

import { useAuthenticator } from '@aws-amplify/ui-react';

const platformRoutes = [
  {
    route: '/',
    component: lazy(() => import('containers/Dashboard')),
    resource: '*',
    action: 'view',
  },
  {
    route: '/integrations',
    component: lazy(() => import('containers/Integration/pages/vendor')),
    resource: '*',
    action: 'view',
  },
  {
    route: '/integrations/:vendor',
    component: lazy(() => import('containers/Integration/pages/integration')),
    resource: '*',
    action: 'view'
  },
  {
    route: '/inventory',
    component: lazy(() => import('containers/Inventory')),
    resource: '*',
    action: 'view',
  },
  {
    route: '/digitalworkers',
    component: lazy(() => import('containers/Application')),
    resource: '*',
    action: 'view'
  },
  {
    route: '/digitalworkers/create',
    component: lazy(() => import('containers/Application/pages/CreateApplication')),
    resource: '*',
    action: 'create'
  },
  {
    route: '/digitalworkers/:applicationId/edit',
    component: lazy(() => import('containers/Application/pages/EditApplication')),
    resource: '*',
    action: 'edit'
  },
  {
    route: '/digitalworkers/:applicationId/view',
    component: lazy(() => import('containers/Application/pages/ViewApplication')),
    resource: '*',
    action: 'view'
  },
  {
    route: '/digitalworkers/:applicationId',
    component: lazy(() => import('containers/Application/pages/NewApplication')),
    resource: '*',
    action: 'view'
  },
  {
    route: '/digitalworkers/executions/:applicationId/:executionId',
    component: lazy(() => import('containers/Application/pages/LiveUpdates')),
    resource: '*',
    action: 'view'
  },
  {
    route: '/observability',
    component: lazy(() => import('containers/Observability')),
    resource: '*',
    action: 'view',
  },
  {
    route: '/observability/views/:viewId/edit',
    component: lazy(() => import('containers/Observability/components/StepTwo')),
    resource: '*',
    action: 'edit',
  },
  {
    route: '/observability/views/create',
    component: lazy(() => import('containers/Observability/components/StepTwo')),
    resource: '*',
    action: 'view',
  },
  {
    route: '/templates',
    component: lazy(() => import('containers/Templates')),
    resource: '*',
    action: 'view',
  },
  {
    route: '/settings/:page',
    component: lazy(() => import('containers/Settings')),
    resource: '*',
    action: 'view'
  },
  {
    route: '/not-found',
    component: NotFound,
    resource: '*',
    action: 'view'
  },
  {
    route: '/unauthorised',
    component: Unauthorised,
    resource: '*',
    action: 'view'
  },
  {
    route: '/forbidden',
    component: Forbidden,
    resource: '*',
    action: 'view'
  },
  {
    route: '/worker-notfound',
    component: WorkerNotFound,
    resource: '*',
    action: 'view'
  }
];

function Routes(props) {
  const [loading, setLoading] = useState(true);
  const [publicView, setPublicView] = useState(false);
  const [statuses, setStatuses] = useState({
    loggedIn: false,
    hasOrgs: false,
  });
  const platform = useStoreState((state) => state.platform);
  const signIn = useStoreActions((actions) => actions.platform.signIn);
  const assignRole = useStoreActions((actions) => actions.platform.assignRole);
  const assignPermissions = useStoreActions((actions) => actions.platform.assignPermissions);
  const setName = useStoreActions((actions) => actions.platform.setName);

  const search = useLocation().search;
  const pathname = useLocation().pathname;

  const { user, authStatus } = useAuthenticator((context) => [context.user]);

  useEffect(() => {
    let orgId = platform.orgId;
    let tenantId = platform.tenantId;

    // Check if the user is on the view page
    const match = matchPath(pathname, {
      path: '/digitalworkers/:applicationId/view',
      exact: true
    });

    if (match && authStatus === 'unauthenticated') {
      setLoading(false);
      setPublicView(true);
    } else if (authStatus === 'authenticated') {
      // check if a user has custom:orgIds and query all orgIds
      // if true, then redirect the user to SelectOrgAndChildTenant page
      // else redirect the user to CompleteSignup page
      if (user?.attributes && user?.attributes['custom:orgIds']) {
        organization.orgs(user.username)
          .then(async ({ status, data }) => {
            if (status === 200) {
              if (data.length === 1 && data[0].childTenants.length === 1) {
                orgId = data[0].orgId;
                tenantId = data[0].childTenants[0].childTenantId;
                signIn({
                  user,
                  userId: user ? user.username : null,
                  orgs: data,
                  orgId,
                  tenantId,
                });
                setStatuses({
                  loggedIn: true,
                  hasOrgs: true,
                });
              } else if (data.length > 0) {
                signIn({
                  user,
                  userId: user ? user.username : null,
                  orgs: data,
                  orgId,
                  tenantId,
                });
                setStatuses({
                  loggedIn: true,
                  hasOrgs: true,
                });
              }

              if (orgId && tenantId) {
                const { data, status } = await organization.user(orgId, user.username);
                if (status === 200) {
                  setName({ firstName: data.firstName, lastName: data.lastName });
                  assignRole(data.role);
    
                  // check if the user has identityId
                  if (!data.identityId) {
                    Auth.currentCredentials().then((credentials) => {
                      organization.update(orgId, user.username, {
                        identityId: credentials.identityId,
                      });
                    });
                  }
                }
    
                const { data: pData, status: pStatus } = await organization.getPermissions(orgId)
                if (pStatus === 200) {
                  assignPermissions(pData);
                }

                setLoading(false);
              } else {
                setLoading(false);
              }
            }
          });
      // else if 
      // user.attributes['custom:src'] === 'campaign'
      // hasOrgs = true
      // 
      } else {
        signIn({
          user,
          userId: null,
          orgs: [],
          orgId: null,
          tenantId: null,
        });
        setStatuses({
          ...statuses,
          loggedIn: true,
        });
        setLoading(false);
      }      
    } else if ((!tenantId || !orgId) && authStatus !== 'configuring') {
      signIn({
        user: null,
        userId: null,
        orgs: [],
        orgId: null,
        tenantId: null,
      });
      setLoading(false);
    } else if (authStatus === 'unauthenticated' && platform.user) {
      signIn({
        user: null,
        userId: null,
        orgs: [],
        orgId: null,
        tenantId: null,
      });
      setLoading(false);
    }
  }, [authStatus]);

  if (loading) {
    return (
      <LoadingOverlay />
    );
  }

  if (publicView) {
    return (
      <Suspense fallback={<LoadingOverlay />}>
        <Route 
          exact
          key="/digitalworkers/:applicationId/view"
          path="/digitalworkers/:applicationId/view"
          layout={PublicLayout}
          component={lazy(() => import('containers/Application/pages/ViewApplication'))}
        />
      </Suspense>
    );
  }

  let view;
  if (statuses.loggedIn) {
    if (statuses.hasOrgs && platform.orgId && platform.tenantId && platform.role && platform.permissions) {
      const redirect_uri = new URLSearchParams(search).get("redirect_uri");
      view = redirect_uri ? <Redirect to={redirect_uri} /> : (
        <Suspense fallback={<LoadingOverlay />}>
          <Switch>
            {platformRoutes.filter((route) => can(platform.role, route.action, route.resource, platform.permissions)).map((item) => (
              <RouteWithLayout
                exact
                key={item.route}
                path={item.route}
                layout={MainLayout}
                component={item.component}
              />
            ))}
            <RouteWithLayout
              exact
              key="/forbidden"
              path="/forbidden"
              layout={MainLayout}
              component={Forbidden}
            />
            <RouteWithLayout
              exact
              key="/worker-notfound"
              path="/worker-notfound"
              layout={MainLayout}
              component={WorkerNotFound}
            />
            <Redirect to="/not-found" />
          </Switch>
        </Suspense>
      );
    } else if (statuses.hasOrgs && (!platform.orgId || !platform.tenantId )) {
      const redirect_uri = new URLSearchParams(search).get("redirect_uri");
      view = (
        <Suspense fallback={<LoadingOverlay />}>
          <Route exact path="/configure" component={SelectOrgAndChildTenant} />
          <Redirect to={redirect_uri ? `/configure?redirect_uri=${redirect_uri}` : '/configure'} />
        </Suspense>
      )
    } else {
      const redirect_uri = new URLSearchParams(search).get("redirect_uri");
      view = (
        <Suspense fallback={<LoadingOverlay />}>
          <Route exact path="/complete-signup" component={CompleteSignup} />
          <Redirect to={redirect_uri ? `/complete-signup?redirect_uri=${redirect_uri}` : '/complete-signup'} />
        </Suspense>
      );
    }
  } else {
    const src = new URLSearchParams(search).get("src");
    const redirect_uri = new URLSearchParams(search).get("redirect_uri");
    view = (
      <Suspense fallback={<LoadingOverlay />}>
        <Switch>
          <Route path="/signin" component={Authentication} />
          <RouteWithLayout
            exact
            key="/unauthorised"
            path="/unauthorised"
            layout={PublicLayout}
            component={Unauthorised}
          />
          <RouteWithLayout
              exact
              key="/worker-notfound"
              path="/worker-notfound"
              layout={PublicLayout}
              component={WorkerNotFound}
            />
          <Redirect to={src ? "/signin?src=campaign" : (redirect_uri ? `/signin?redirect_uri=${redirect_uri}` : "/signin")} />
        </Switch>
      </Suspense>
    );
  }

  return view;
}

export default Routes;
